import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { ActivityIndicator, Text, useTheme } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useMapData } from '@/hooks/monitor/useMapData'
import type { LocationCircle } from '@/hooks/useActiveLocations'
import { waitForGoogleMapsReady } from '@/hooks/useIsGoogleMapsReady'
import { hexToRgba } from '@/lib/color-utils'
import { haversineDistance } from '@/lib/map-utils'
import {
    useMapHeading,
    useMapZoom,
    useMonitorStore,
    useMonitorUIState,
    useSelectedLocation,
} from '@/stores/monitor-store'
import type { CircleMarkerData, MapRegion } from '@/types/mapData'
import type { UserLocationWithStatus } from '@/types/movement'
import type MapViewType from '@/ui/MapView'
import MapView, { Circle } from '@/ui/MapView'
import { UserPathMarkers } from '@/ui/map/PathMarkers'
import { LocationActivityDrawer } from '@/ui/monitor/LocationActivityDrawer'
import { OnSiteClusterBadge } from '../map/OnSiteClusterBadge'
import { UserStatusMarker } from '../map/UserStatusMarker'

interface Props {
    userLocations: UserLocationWithStatus[]
    locationCircles: LocationCircle[]
    selectedLocation: LocationCircle | null
    selectedLocationActivity: ReturnType<
        typeof import('@/hooks/useGeofenceActivity').useGeofenceActivity
    > extends infer R
        ? R extends { selectedLocationActivity: infer A }
            ? A
            : never
        : never
    isGeofenceDataPending: boolean
    userColorMap: Map<string, string>
    isPending: boolean
}

function findCircleAtCoordinate(
    circles: CircleMarkerData[],
    latitude: number,
    longitude: number
): CircleMarkerData | null {
    for (const circle of circles) {
        const distance = haversineDistance(
            latitude,
            longitude,
            circle.center.latitude,
            circle.center.longitude
        )
        if (distance <= circle.radius) return circle
    }
    return null
}

export function MonitorMapView({
    userLocations,
    locationCircles,
    selectedLocation,
    selectedLocationActivity,
    isGeofenceDataPending,
    userColorMap,
    isPending,
}: Props) {
    const theme = useTheme()
    const semantic = useSemanticColors()
    const mapRef = useRef<MapViewType>(null)

    const { selectedLocationId, setSelectedLocation } = useSelectedLocation()
    const shouldAutoUpdateView = useMonitorStore(state => state.shouldAutoUpdateView)
    const { setShouldAutoUpdateView } = useMonitorUIState()
    const { setZoomLevel } = useMapZoom()
    const { setMapHeading } = useMapHeading()
    const focusedUserId: string | null = null

    const mapData = useMapData(userLocations, locationCircles, { clusterOnSite: false })

    const isMapReadyRef = useRef(false)

    const animateToRegion = useCallback((region: MapRegion, duration = 500) => {
        waitForGoogleMapsReady().then(() => {
            mapRef.current?.animateToRegion(region, duration)
        })
    }, [])

    const handleRegionChange = useCallback(
        (region: MapRegion) => {
            setZoomLevel(region.latitudeDelta)
            mapRef.current?.getCamera().then(camera => {
                setMapHeading(camera.heading)
            })
        },
        [setZoomLevel, setMapHeading]
    )

    // Re-pan/zoom whenever the computed region changes. Without this the map
    // only applies its initial camera on first render — Google Maps ignores
    // subsequent initialRegion changes — and the detail view stays at world
    // zoom.
    useEffect(() => {
        if (!isMapReadyRef.current) return
        if (mapData.userMarkers.length === 0 && mapData.onSiteClusters.length === 0) return
        animateToRegion(mapData.region)
    }, [mapData.region, mapData.userMarkers.length, mapData.onSiteClusters.length, animateToRegion])

    useEffect(() => {
        if (!shouldAutoUpdateView) return
        if (userLocations.length === 0) return
        setShouldAutoUpdateView(false)
        animateToRegion(mapData.region)
    }, [
        shouldAutoUpdateView,
        userLocations.length,
        mapData.region,
        setShouldAutoUpdateView,
        animateToRegion,
    ])

    const onMapReady = useCallback(() => {
        isMapReadyRef.current = true
        if (mapData.userMarkers.length === 0 && mapData.onSiteClusters.length === 0) return
        animateToRegion(mapData.region)
    }, [mapData.region, mapData.userMarkers.length, mapData.onSiteClusters.length, animateToRegion])

    // biome-ignore lint/correctness/useExhaustiveDependencies: focusedUserId is read on the early return and inside the .find — biome misses it.
    useEffect(() => {
        if (!focusedUserId) return
        const user = userLocations.find(u => u.userId === focusedUserId)
        if (!user?.mostRecentLocation) return
        animateToRegion({
            latitude: user.mostRecentLocation.latitude,
            longitude: user.mostRecentLocation.longitude,
            latitudeDelta: 0.01,
            longitudeDelta: 0.01,
        })
    }, [focusedUserId, userLocations, animateToRegion])

    const handleMapPress = useCallback(
        (event: { nativeEvent: { coordinate: { latitude: number; longitude: number } } }) => {
            if (Platform.OS === 'web') return
            const { latitude, longitude } = event.nativeEvent.coordinate
            const circle = findCircleAtCoordinate(mapData.circles, latitude, longitude)
            if (circle) setSelectedLocation(circle.id)
        },
        [mapData.circles, setSelectedLocation]
    )

    const referenceTime = useMemo(() => new Date(), [])
    const zoomLevel = useMonitorStore(state => state.zoomLevel)
    const mapHeading = useMonitorStore(state => state.mapHeading)

    // Web's @teovilla/react-native-web-maps ignores initialRegion.latitudeDelta
    // and hardcodes zoom=3 unless initialCamera is supplied. Derive a reasonable
    // starting zoom from the fitted region so the map doesn't open at world view.
    const initialCamera = useMemo(() => {
        const delta = Math.max(mapData.region.latitudeDelta, 0.001)
        const zoom = Math.max(0, Math.min(21, Math.round(Math.log2(360 / delta))))
        return {
            center: {
                latitude: mapData.region.latitude,
                longitude: mapData.region.longitude,
            },
            zoom,
            heading: 0,
            pitch: 0,
            altitude: 0,
        }
    }, [mapData.region])

    return (
        <View style={styles.container}>
            <MapView
                ref={mapRef}
                style={styles.map}
                initialRegion={mapData.region}
                {...(Platform.OS === 'web' ? ({ initialCamera } as Record<string, unknown>) : {})}
                onMapReady={onMapReady}
                showsUserLocation={false}
                showsMyLocationButton={false}
                showsCompass={false}
                showsTraffic={false}
                showsBuildings={false}
                showsIndoors={false}
                toolbarEnabled={false}
                onRegionChange={handleRegionChange}
                onPress={handleMapPress}
            >
                {mapData.circles.map(circle => {
                    const isSelected = selectedLocationId === circle.id
                    return (
                        <Circle
                            key={circle.id}
                            center={circle.center}
                            radius={circle.radius}
                            strokeColor={isSelected ? semantic.successBorder : semantic.success}
                            strokeWidth={isSelected ? 3 : 2}
                            fillColor={
                                isSelected
                                    ? hexToRgba(semantic.successBorder, 0.25)
                                    : hexToRgba(semantic.success, 0.1)
                            }
                            {...(Platform.OS === 'web'
                                ? ({
                                      onPress: () => setSelectedLocation(circle.id),
                                  } as Record<string, unknown>)
                                : {})}
                        />
                    )
                })}
                <UserPathMarkers
                    paths={mapData.userPaths}
                    mapHeading={mapHeading}
                    focusedUserId={focusedUserId}
                    zoomLevel={zoomLevel}
                    referenceTime={referenceTime}
                />
                {mapData.userMarkers.map(marker => (
                    <UserStatusMarker
                        key={marker.userId}
                        marker={marker}
                        focusedUserId={focusedUserId}
                    />
                ))}
                {mapData.onSiteClusters.map(cluster => (
                    <OnSiteClusterBadge key={cluster.siteJobId} cluster={cluster} />
                ))}
            </MapView>

            {isPending && (
                <View
                    style={[
                        styles.overlay,
                        { backgroundColor: hexToRgba(theme.colors.surface, 0.85) },
                    ]}
                >
                    <ActivityIndicator size="large" />
                    <Text style={styles.overlayText}>Loading movement data…</Text>
                </View>
            )}

            <LocationActivityDrawer
                isVisible={!!selectedLocation}
                location={selectedLocation}
                activity={selectedLocationActivity}
                isPending={isGeofenceDataPending}
                userColorMap={userColorMap}
            />
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        position: 'relative',
        minHeight: 600,
    },
    map: { flex: 1 },
    overlay: {
        position: 'absolute',
        inset: 0,
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
    },
    overlayText: {
        fontSize: 16,
        fontWeight: '500',
    },
})
